import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  FileText,
  Gift,
  Home,
  LogOut,
  Receipt,
  Ticket,
  User,
  ClipboardList,
  Calendar,
} from 'lucide-react';
import { usePortalAuthStore } from '@/stores/portal-auth.js';
import { cn } from '@/lib/utils.js';

const nav = [
  { to: '/portal/dashboard', label: 'Dashboard', icon: Home },
  { to: '/portal/tickets', label: 'My Repairs', icon: Ticket },
  { to: '/portal/invoices', label: 'Invoices', icon: Receipt },
  { to: '/portal/estimates', label: 'Estimates', icon: ClipboardList },
  { to: '/portal/appointments', label: 'Appointments', icon: Calendar },
  { to: '/portal/loyalty', label: 'Loyalty & Credits', icon: Gift },
  { to: '/portal/profile', label: 'My Profile', icon: User },
];

export function PortalLayout() {
  const customer = usePortalAuthStore((s) => s.customer);
  const clearAuth = usePortalAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  const name = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || 'Account';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-sky-50 text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="font-display text-lg font-bold tracking-tight text-[#1B4F8A]">
            Customer Portal
          </div>
          <div className="relative group">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm"
            >
              <FileText className="h-4 w-4 text-[#1B4F8A]" />
              {name}
            </button>
            <div className="invisible absolute right-0 mt-1 w-48 rounded-xl border border-slate-200 bg-white py-1 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100">
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => navigate('/portal/profile')}
              >
                My Profile
              </button>
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => navigate('/portal/profile/change-password')}
              >
                Change Password
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  clearAuth();
                  navigate('/portal/login');
                }}
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-6 px-4 py-6 pb-24 md:pb-6">
        <aside className="hidden w-56 shrink-0 md:block">
          <nav className="space-y-1 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition',
                    isActive
                      ? 'bg-[#1B4F8A] text-white'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950',
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur md:hidden">
        <div className="grid grid-cols-5 gap-1 px-1 py-1">
          {nav.slice(0, 5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-0.5 rounded-lg px-1 py-2 text-[10px] font-semibold',
                  isActive ? 'text-[#1B4F8A]' : 'text-slate-500',
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label.split(' ')[0]}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
